import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminApi, eventsApi, clientSelfImprovementApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { AITraceItem, AITraceDetailResponse, Event, AppUser } from '../../types';

type TabKey = 'knowledge_gap' | 'guardrail' | 'closed';
type ModalTab = 'overview' | 'prompt' | 'response' | 'timing' | 'usage';

interface TabDef {
  key: TabKey;
  label: string;
  answered?: boolean;
  refusalReason?: string;
  resolutionStatus?: string;
}

const TABS: TabDef[] = [
  { key: 'knowledge_gap', label: 'Knowledge Gap', answered: false, refusalReason: 'knowledge_gap', resolutionStatus: 'unresolved' },
  { key: 'guardrail',     label: 'Guardrail',     answered: false, refusalReason: 'guardrail',     resolutionStatus: 'unresolved' },
  { key: 'closed',        label: 'Resolved',      resolutionStatus: 'resolved' },
];

const MODAL_TABS: { key: ModalTab; label: string }[] = [
  { key: 'overview',  label: 'Overview' },
  { key: 'prompt',    label: 'Prompt' },
  { key: 'response',  label: 'Response' },
  { key: 'timing',    label: 'Timing' },
  { key: 'usage',     label: 'Usage / Cost' },
];

const PAGE_LIMIT = 50;

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function truncate(s: string | undefined | null, n: number) {
  if (!s) return '—';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

function prettyJson(v: any) {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-sm py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-gray-500 w-40 shrink-0">{label}</span>
      <span className="text-gray-900 font-medium break-all">{value ?? '—'}</span>
    </div>
  );
}

function Badge({ value, map }: { value: string | null | undefined; map: Record<string, string> }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const cls = map[value] ?? 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
      {value}
    </span>
  );
}

export default function SelfImprovement() {
  const { isAdmin, user } = useAuth();

  const traceApi = {
    listAITraces: isAdmin ? adminApi.listAITraces.bind(adminApi) : clientSelfImprovementApi.listTraces.bind(clientSelfImprovementApi),
    getAITrace: isAdmin ? adminApi.getAITrace.bind(adminApi) : clientSelfImprovementApi.getTrace.bind(clientSelfImprovementApi),
    updateAITraceResolution: isAdmin ? adminApi.updateAITraceResolution.bind(adminApi) : clientSelfImprovementApi.updateTraceResolution.bind(clientSelfImprovementApi),
  };

  const [activeTab, setActiveTab] = useState<TabKey>('knowledge_gap');
  const [traces, setTraces] = useState<AITraceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [updating, setUpdating] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  // Client / KA filters
  const [clients, setClients] = useState<AppUser[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTrace, setModalTrace] = useState<AITraceItem | null>(null);
  const [modalTab, setModalTab] = useState<ModalTab>('overview');
  const [detailLoading, setDetailLoading] = useState(false);
  const [llmDetail, setLlmDetail] = useState<AITraceDetailResponse | null>(null);
  const [llmTrace, setLlmTrace] = useState<AITraceItem | null>(null);

  const currentTab = TABS.find(t => t.key === activeTab)!;

  const fetchClients = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingClients(true);
    try {
      const resp = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      setClients((resp.data as any)?.data?.users || []);
    } catch { /* ignore */ } finally {
      setLoadingClients(false);
    }
  }, [isAdmin]);

  const fetchEvents = useCallback(async (clientId?: string) => {
    try {
      const params: any = { limit: 100 };
      if (isAdmin && clientId) params.clientId = clientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      const resp = await eventsApi.getAll(params);
      setEvents(resp.data.data || []);
    } catch { /* ignore */ }
  }, [isAdmin, user?._id]);

  // On mount
  useEffect(() => {
    if (isAdmin) fetchClients();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // When admin changes client filter → reload events, reset KA selection
  useEffect(() => {
    if (!isAdmin) return;
    setSelectedEventId('');
    fetchEvents(selectedClientId || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  const fetchTraces = useCallback(async (tab: TabDef, p: number, eventId: string, clientId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await traceApi.listAITraces({
        ...(tab.answered !== undefined ? { answered: tab.answered } : {}),
        ...(tab.refusalReason ? { refusalReason: tab.refusalReason } : {}),
        ...(tab.resolutionStatus ? { resolutionStatus: tab.resolutionStatus } : {}),
        ...(eventId ? { eventId } : {}),
        ...(isAdmin && clientId ? { clientId } : {}),
        page: p,
        limit: PAGE_LIMIT,
      });
      const data = res.data?.data;
      setTraces(data?.traces ?? []);
      setTotalPages(data?.pagination?.pages ?? 1);
      setTotal(data?.pagination?.total ?? 0);
    } catch {
      setError('Failed to load traces. Please try again.');
      setTraces([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
    fetchTraces(currentTab, 1, selectedEventId, selectedClientId);
  }, [activeTab, selectedEventId, selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTraces(currentTab, page, selectedEventId, selectedClientId);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openModal(trace: AITraceItem) {
    setModalTrace(trace);
    setModalTab('overview');
    setLlmDetail(null);
    setLlmTrace(null);
    setModalOpen(true);
    setDetailLoading(true);

    try {
      // Fetch sibling traces for this message to find the chat_llm_stream / chat_llm trace
      const siblingRes = await traceApi.listAITraces({ messageId: trace.messageId, limit: 20 });
      const siblings: AITraceItem[] = siblingRes.data?.data?.traces ?? [];
      const llm = siblings.find(
        t => t.operation === 'chat_llm_stream' || t.operation === 'chat_llm'
      );
      if (llm) {
        setLlmTrace(llm);
        const detail = await traceApi.getAITrace(llm.traceId);
        setLlmDetail(detail.data?.data ?? null);
      }
    } catch {
      // detail stays null — graceful degradation
    } finally {
      setDetailLoading(false);
    }
  }

  function closeModal() {
    setModalOpen(false);
    setModalTrace(null);
    setLlmDetail(null);
    setLlmTrace(null);
  }

  async function handleResolutionChange(traceId: string, newStatus: 'resolved' | 'not_resolved') {
    setUpdating(u => ({ ...u, [traceId]: true }));
    const prev = traces.find(t => t.traceId === traceId)?.resolutionStatus;
    setTraces(ts => ts.map(t => t.traceId === traceId ? { ...t, resolutionStatus: newStatus } : t));
    if (modalTrace?.traceId === traceId) {
      setModalTrace(mt => mt ? { ...mt, resolutionStatus: newStatus } : mt);
    }
    try {
      await traceApi.updateAITraceResolution(traceId, newStatus);
    } catch {
      setTraces(ts => ts.map(t => t.traceId === traceId ? { ...t, resolutionStatus: prev } : t));
      if (modalTrace?.traceId === traceId) {
        setModalTrace(mt => mt ? { ...mt, resolutionStatus: prev } : mt);
      }
    } finally {
      setUpdating(u => ({ ...u, [traceId]: false }));
    }
  }

  const emptyLabel = {
    knowledge_gap: 'No knowledge gap entries found.',
    guardrail: 'No guardrail entries found.',
    closed: 'No resolved entries found.',
  }[activeTab];

  // Build modal tab content
  const usage = llmTrace?.usage as any;
  const timingParent = modalTrace?.timingMs as any;
  const timingLlm = llmTrace?.timingMs as any;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Self-Improvement</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review unanswered questions, identify knowledge gaps and guardrail triggers, and track resolution.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <select
              value={selectedClientId}
              onChange={e => setSelectedClientId(e.target.value)}
              disabled={loadingClients}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c._id} value={c._id}>{c.name || c.email}</option>
              ))}
            </select>
          )}
          <select
            value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
          >
            <option value="">All Knowledge Assistants</option>
            {events.map(e => (
              <option key={e._id} value={e._id}>{e.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200">
          <div className="flex">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Loading…</div>
          ) : error ? (
            <div className="flex items-center justify-center py-16 text-red-500 text-sm">{error}</div>
          ) : traces.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">{emptyLabel}</div>
          ) : (
            <>
              <div className="text-xs text-gray-400 mb-3">{total} total</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left py-2 pr-4 font-medium w-32">Date</th>
                      <th className="text-left py-2 pr-4 font-medium">Question Asked</th>
                      <th className="text-left py-2 pr-4 font-medium">Detail</th>
                      <th className="text-left py-2 pr-4 font-medium w-36">User</th>
                      <th className="text-left py-2 font-medium w-40">Resolution</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {traces.map(trace => (
                      <tr
                        key={trace.traceId}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => openModal(trace)}
                      >
                        <td className="py-3 pr-4 text-gray-500 text-xs whitespace-nowrap">
                          {formatDate(trace.createdAt)}
                        </td>
                        <td className="py-3 pr-4 text-gray-800 max-w-xs">
                          <span title={trace.request?.userMessage ?? ''}>
                            {truncate(trace.request?.userMessage, 80)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-gray-600 max-w-xs">
                          <span title={trace.unansweredDetail ?? ''}>
                            {truncate(trace.unansweredDetail, 60)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-gray-500 text-xs">
                          {trace.userId ?? '—'}
                        </td>
                        <td
                          className="py-3"
                          onClick={e => e.stopPropagation()}
                        >
                          {activeTab === 'closed' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                              Resolved
                            </span>
                          ) : (
                            <select
                              disabled={updating[trace.traceId]}
                              value={trace.resolutionStatus ?? 'not_resolved'}
                              onChange={e => handleResolutionChange(trace.traceId, e.target.value as 'resolved' | 'not_resolved')}
                              className={`text-xs border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                                trace.resolutionStatus === 'resolved'
                                  ? 'border-green-300 text-green-700 bg-green-50'
                                  : 'border-gray-300 text-gray-700 bg-white'
                              } ${updating[trace.traceId] ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <option value="not_resolved">Not resolved</option>
                              <option value="resolved">Resolved</option>
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {modalOpen && modalTrace && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Trace Detail</h3>
                <p className="text-xs text-gray-400 mt-0.5">{formatDate(modalTrace.createdAt)}</p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            {/* Modal tabs */}
            <div className="border-b border-gray-200 shrink-0">
              <div className="flex overflow-x-auto px-5">
                {MODAL_TABS.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setModalTab(t.key)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                      modalTab === t.key
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto p-5">
              {detailLoading ? (
                <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Loading details…</div>
              ) : (
                <>
                  {/* OVERVIEW */}
                  {modalTab === 'overview' && (
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Quality Signal</h4>
                        <MetaRow label="Answered" value={
                          <Badge value={String(modalTrace.answered)} map={{
                            'true': 'bg-green-50 text-green-700 border-green-200',
                            'false': 'bg-red-50 text-red-700 border-red-200',
                            'null': 'bg-gray-100 text-gray-500 border-gray-200',
                          }} />
                        } />
                        <MetaRow label="Reason" value={
                          <Badge value={modalTrace.refusalReason} map={{
                            knowledge_gap: 'bg-amber-50 text-amber-700 border-amber-200',
                            guardrail: 'bg-red-50 text-red-700 border-red-200',
                          }} />
                        } />
                        <MetaRow label="Detail" value={modalTrace.unansweredDetail} />
                        <MetaRow label="Resolution" value={
                          activeTab === 'closed' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">Resolved</span>
                          ) : (
                            <select
                              disabled={updating[modalTrace.traceId]}
                              value={modalTrace.resolutionStatus ?? 'not_resolved'}
                              onChange={e => handleResolutionChange(modalTrace.traceId, e.target.value as 'resolved' | 'not_resolved')}
                              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            >
                              <option value="not_resolved">Not resolved</option>
                              <option value="resolved">Resolved</option>
                            </select>
                          )
                        } />
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Message</h4>
                        <MetaRow label="Question Asked" value={modalTrace.request?.userMessage ?? '—'} />
                        <MetaRow label="User" value={modalTrace.userId ?? '—'} />
                        <MetaRow label="Created" value={formatDate(modalTrace.createdAt)} />
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Model</h4>
                        <MetaRow label="Operation" value={<code className="text-xs bg-white border border-gray-200 px-1.5 py-0.5 rounded">{llmTrace?.operation ?? modalTrace.operation}</code>} />
                        <MetaRow label="Provider" value={llmTrace?.provider ?? modalTrace.provider} />
                        <MetaRow label="Model" value={llmTrace?.model ?? modalTrace.model} />
                        <MetaRow label="Success" value={
                          <Badge value={(llmTrace?.success ?? modalTrace.success) ? 'true' : 'false'} map={{
                            true: 'bg-green-50 text-green-700 border-green-200',
                            false: 'bg-red-50 text-red-700 border-red-200',
                          }} />
                        } />
                      </div>
                    </div>
                  )}

                  {/* PROMPT */}
                  {modalTab === 'prompt' && (
                    <div>
                      {llmDetail?.payloads?.prompt ? (
                        <pre className="whitespace-pre-wrap break-words text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-[60vh] overflow-y-auto">
                          {llmDetail.payloads.prompt}
                        </pre>
                      ) : (
                        <p className="text-sm text-gray-400 py-8 text-center">
                          {llmDetail ? 'No prompt data recorded.' : 'Prompt data unavailable — child trace not found.'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* RESPONSE */}
                  {modalTab === 'response' && (
                    <div>
                      {llmDetail?.payloads?.response ? (
                        <pre className="whitespace-pre-wrap break-words text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-[60vh] overflow-y-auto">
                          {llmDetail.payloads.response}
                        </pre>
                      ) : (
                        <p className="text-sm text-gray-400 py-8 text-center">
                          {llmDetail ? 'No response data recorded.' : 'Response data unavailable — child trace not found.'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* TIMING */}
                  {modalTab === 'timing' && (
                    <div className="space-y-4">
                      {timingParent && Object.keys(timingParent).length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Request Timing (ms)</h4>
                          {Object.entries(timingParent).map(([k, v]) => (
                            <MetaRow key={k} label={k} value={v != null ? `${v} ms` : '—'} />
                          ))}
                        </div>
                      )}
                      {timingLlm && Object.keys(timingLlm).length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">LLM Timing (ms)</h4>
                          {Object.entries(timingLlm).map(([k, v]) => (
                            <MetaRow key={k} label={k} value={v != null ? `${v} ms` : '—'} />
                          ))}
                        </div>
                      )}
                      {!timingParent && !timingLlm && (
                        <p className="text-sm text-gray-400 py-8 text-center">No timing data available.</p>
                      )}
                    </div>
                  )}

                  {/* USAGE / COST */}
                  {modalTab === 'usage' && (
                    <div className="space-y-4">
                      {usage ? (
                        <>
                          {usage.usageMetadata && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Token Usage</h4>
                              <MetaRow label="Prompt tokens" value={usage.usageMetadata.promptTokenCount ?? usage.usageMetadata.input_tokens} />
                              <MetaRow label="Completion tokens" value={usage.usageMetadata.candidatesTokenCount ?? usage.usageMetadata.completion_tokens ?? usage.usageMetadata.output_tokens} />
                              <MetaRow label="Total tokens" value={usage.usageMetadata.totalTokenCount ?? usage.usageMetadata.total_tokens} />
                            </div>
                          )}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Raw Usage Object</h4>
                            <pre className="whitespace-pre-wrap break-words text-xs bg-white border border-gray-200 rounded p-3 max-h-60 overflow-y-auto">
                              {prettyJson(usage)}
                            </pre>
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-400 py-8 text-center">No usage data available.</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
